<template>
  <div class="BaseInput">
    <label
      v-if="label"
      class="BaseInput__label uppercase"
      data-cy="base-input-label"
      :for="labelFor"
    >
      <slot />
    </label>
    <input
      @input="emitInput"
      @keydown="$emit('keydown', $event)"
      autocomplete="off"
      :class="['BaseInput__input fs-14', {
        'BaseInput__input--error': !valid,
        'BaseInput__input--borderless': borderless && valid,
      }]"
      data-cy="base-input-input"
      :disabled="disabled"
      :id="labelFor"
      :maxlength="maxLength"
      :minlength="minLength"
      :placeholder="placeholder"
      :type="dataType"
      :value="value"
    >
    <i
      v-if="type === 'password'"
      @click="toggleInputType"
      class="fa fa-view fa--none fc-light clickable"
      data-cy="base-input-password-view-icon"
    />
    <transition name="Transition__fade">
      <label
        v-if="!valid"
        class="fc-red fs-12"
        data-cy="base-input-instructions"
      >
        {{ instructions }}
      </label>
    </transition>
  </div>
</template>

<script>
// Helpers
import {
  convertDollarsToBig,
} from '../../../shared/calculations'
import {
  cleanUserInput,
} from '../utils/helpers'

export default {
  name: 'BaseInput',

  props: {
    borderless: {
      type: Boolean,
      required: false,
      default: false,
    },
    currency: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    instructions: {
      type: String,
      required: false,
      default: null,
    },
    label: {
      type: Boolean,
      required: false,
      default: false,
    },
    labelFor: {
      type: String,
      required: false,
      default: null,
    },
    maxLength: {
      type: Number,
      required: false,
      default: 255,
    },
    minLength: {
      type: Number,
      required: false,
      default: null,
    },
    placeholder: {
      type: String,
      required: false,
      default: null,
    },
    type: {
      type: String,
      required: false,
      default: 'text',
    },
    valid: {
      type: Boolean,
      required: false,
      default: true,
    },
    // Required if currency is true
    // If currency is true, prop value should be the emitted .cleaned
    // TODO: remove Number
    value: {
      type: [ Number, String ],
      required: false,
      default: null,
    },
    allowNegative: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data () {
    return {
      // Used to toggle password visibility (does not affect other types of inputs)
      dataType: this.type,
    }
  },

  methods: {
    emitInput ($event) {
      /*
       * For instances where we're capturing user input representing a financial value
       * (dollars), we want to use a single component (single source of truth) and a
       * single function to clean that user input of all input that we don't want when
       * converting the user input to a big instance
       *
       * If the "currency" prop isn't true, then nothing is different; We're simply emitting
       * the user input.
       *
       * If the "currency" prop is true, we're cleaning the user input and emitting an object
       * with the cleaned user input and the clean input as a big instance.
       */
      let emittedInput = $event.target.value.trim()

      if (this.currency) {
        let cleanedInput = cleanUserInput(emittedInput)
        if (this.$props.allowNegative && emittedInput.startsWith('-')) {
          cleanedInput = `-${cleanedInput}`
        }
        // Only modify emittedInput if cleanedInput isn't null (i.e. input "ff" would be null)
        // because big will throw an [invalid number] error if a non-number is passed
        if (emittedInput && cleanedInput) {
          // If a user types two "."'s in a currency input which is converted by Big for the .big
          // emitted property, Big will throw an error for an invalid number (param) to a Big
          // function call, so if there is an error converting the input to a Big instance, return
          // null for .big. We'll then be using .cleaned for our validation checks and .big only for
          // calculations (wrapped in try...catch blocks in case of errors there).
          let bigValue

          try {
            bigValue = emittedInput === '-'
              ? convertDollarsToBig(0)
              : convertDollarsToBig(cleanedInput)
          }
          catch (err) {
            this.CError(err)
            bigValue = null
          }

          emittedInput = {
            big: bigValue,
            cleaned: cleanedInput,
          }
        }
        else {
          emittedInput = ''
        }
      }

      this.$emit('input', emittedInput)
    },

    // Password visibility toggling
    toggleInputType () {
      if (this.type !== 'password') {
        return
      }
      // eslint-disable-next-line no-unused-expressions
      this.dataType === 'password'
        ? this.dataType = 'text'
        : this.dataType = 'password'
    },
  },
}
</script>

<style lang="sass">

.BaseInput
  +flex($direction: column)
  $block: &
  flex-shrink: 0
  position: relative

  .fa-view
    padding: rem(11px) rem(16px)
    position: absolute
    right: 0
    top: rem(25px)

  &__input
    background-color: $white
    border: $border
    border-radius: $border-radius
    color: #3E3F42
    outline: none
    padding: rem(7px) 1rem
    transition: all .15s ease 0s
    width: 100%

    &:invalid
      box-shadow: none

    &:focus,
    &:hover
      border-color: $branding

    &::placeholder
      color: #AFB1B5
      font-size: inherit

    &:disabled
      color: $text-light

      &:focus,
      &:hover
        border-color: $borders

    &--borderless
      border: none

    &--medium
      width: 270px

    // We want to keep the border red until the input is no longer invalid
    &--error
      border-color: $danger !important

  &__label,
  &__label span
    color: #9EA0A5
    font-size: rem(12px)
    line-height: rem(18px)
    margin-bottom: rem(7px)
    user-select: none
</style>
